import { inventaApi } from '@vectronic/desktop-api';
import { firstValueFrom, from, isObservable } from 'rxjs';

export class DesktopInterceptor {
  private static snackbarSuccessHandle?: (error: string) => void;
  private static snackbarErrorHandle?: (error: string) => void;
  private static snackbarInfoHandle?: (error: string) => void;
  private static permissionRequestHandle?: (ids: number[]) => void;

  static setHandles(handles: {
    info: (message: string) => void;
    error: (message: string) => void;
    success: (message: string) => void;
    permissionRequest: (ids: number[]) => void;
  }) {
    this.snackbarInfoHandle = handles.info;
    this.snackbarErrorHandle = handles.error;
    this.snackbarSuccessHandle = handles.success;
    this.permissionRequestHandle = handles.permissionRequest;
  }

  static init() {
    const { _desktop, mobile } = window as any;
    if (_desktop) window.desktop = {} as any;
    const source = _desktop || mobile;
    const target = window.desktop || mobile;
    for (const key in source) {
      if (_desktop) target[key] = {};
      for (const endpoint in source[key]) {
        const incepted = this.interceptor(key, endpoint, source[key][endpoint]);
        const isOn = endpoint.startsWith('on');
        target[key][endpoint] = isOn ? incepted : (...args: any) => from(incepted(...args));
      }
    }
  }

  private static interceptor(key: string, endpoint: string, call: (..._args: any[]) => any) {
    return async (...args: any) => {
      const doLogCall = false;
      const doLogError = false;
      const doSnackbarError = true;
      const doSnackbarCustom = true;

      if (doLogCall) this.logCall(`${key}.${endpoint}`, args);

      try {
        const rawResult = call(...args);
        const result = isObservable(rawResult) ? await firstValueFrom(rawResult) : await rawResult;
        if (result?.success === false) throw result.error;
        if (doSnackbarCustom) {
          const successMessage = this.customMessages[key]?.[endpoint]?.(
            undefined,
            ...args,
          )?.success;
          if (successMessage) this.snackbarSuccessHandle?.(successMessage);
        }
        return result;
      } catch (error: any) {
        if (error == null) throw error;
        if (doLogError) console.log(error);
        if (doSnackbarError) this.handleError(key, endpoint, error, args);
        throw error;
      }
    };
  }

  private static logCall(channel: string, args: any[]) {
    const isHidden = [
      'deadReckoning.getPositions',
      'deadReckoning.onStatus',
      'app.createMarkerIconFile',
      'app.getMissingDrivers',
    ].includes(channel);
    if (isHidden) return;
    const displayArgs = channel.startsWith('apiKeys.')
      ? []
      : args.filter((arg: any) => ['boolean', 'number', 'string'].includes(typeof arg));
    console.log(`[desktopInterceptor] ${channel} ${displayArgs}`);
  }

  private static handleError(key: string, endpoint: string, error: any, args: any[]) {
    const defaultErrorMessage = error?.message ?? String(error);
    const customMessage = this.customMessages[key]?.[endpoint]?.(defaultErrorMessage, ...args);
    if (customMessage?.permissionRequest) {
      this.permissionRequestHandle?.(customMessage.permissionRequest);
    } else if (customMessage?.info) {
      this.snackbarInfoHandle?.(customMessage.info);
    } else {
      const errorMessage = customMessage?.error;
      if (errorMessage !== this.noSnackBar())
        this.snackbarErrorHandle?.(errorMessage ?? defaultErrorMessage);
    }
  }

  private static noSnackBar() {
    return 'noSnackBarErrorMessage';
  }
  private static hideCancel(error: string, errorMessage: string) {
    if (!error) return;
    const lower = error.toLowerCase();
    return lower.includes('cancel') || lower.includes('abort') ? this.noSnackBar() : errorMessage;
  }

  static isWarning(error: string) {
    return this.warningTags().some((tag) => error.includes(tag));
  }
  private static warningTags() {
    return [': server', ': client', 'need to be online'] as const;
  }
  static readDataWarning(error: string) {
    if (!error) return;
    let warn: string | undefined;
    for (const tag of this.warningTags()) {
      if (error.includes(tag)) {
        let cause = error;
        if (error.includes('too many events')) cause = 'Upload limit for this collar reached';
        else if (error.includes('too many requests'))
          cause = 'current upload limit reached. Please try again later';
        warn = `Data download successful. No data upload to Inventa Web. Cause: ${cause}.`;
        break;
      }
    }
    return warn;
  }

  //NOTE default is no success message
  //NOTE default is the error as error message, setting to undefined surpresses it
  private static customMessages: inventaApi.MessageMap = {
    deadReckoning: {
      get: () => ({ success: undefined, error: this.noSnackBar() }),
      delete: (error) => ({
        success: `Session calibration deleted successfully.`,
        error: `Failed to delete session calibration. ${this.parsedError(error)}`,
      }),
      csvExport: (error) => ({
        success: `CSV export completed successfully.`,
        error: this.hideCancel(error, `Failed to export CSV. ${this.parsedError(error)}`),
      }),
    },
    motion: {
      getSharedFiles: () => ({ success: undefined, error: this.noSnackBar() }),
      sharepointDownload: (error, name) => ({
        success: `Downloaded motion file ${name} successfully.`,
        error: this.hideCancel(
          error,
          `Failed to download motion file ${name}. ${this.parsedError(error)}`,
        ),
      }),
      deleteSession: (error) => ({
        success: `Session deleted successfully.`,
        error: `Failed to delete session. ${this.parsedError(error)}`,
      }),
      csvExport: (error, { sessions: sessionGroup }) => ({
        success: `${sessionGroup[0].dataType} CSV export of collar ${sessionGroup[0].collarId} completed successfully.`,
        error: this.hideCancel(error, `Failed to export CSV. ${this.parsedError(error)}`),
      }),
      binExport: (error) => ({
        success: `Binary export completed successfully.`,
        error: this.hideCancel(error, `Failed to export binary data. ${this.parsedError(error)}`),
      }),
      binImport: (error) => {
        if (error?.includes('unauthorized')) {
          const ids = Array.from(
            new Set([...error.matchAll(/(?<!\d)(\d+)\s+unauthorized/g)].map((m) => m[1])),
          ).map((id) => Number(id));
          return { permissionRequest: ids };
        }
        return {
          success: `Binary import completed successfully.`,
          error: this.hideCancel(error, `Failed to import binary data. ${this.parsedError(error)}`),
        };
      },
    },
    command: {
      getScheduleTemplates: (error) => ({
        success: undefined,
        error: error?.includes('need to be online')
          ? this.noSnackBar()
          : `Failed to get schedule templates. ${this.parsedError(error)}`,
      }),
      scheduleFromXmlString: (error) => ({
        success: undefined,
        error: error?.includes('Default skip count is not supported in INVENTA')
          ? this.noSnackBar()
          : `Failed to import schedule. ${this.parsedError(error)}`,
      }),
    },
    connectionCall: {
      readData: (error, { deviceId }, dataType) => ({
        success: `${dataType} data from device ${deviceId} read successfully.`,
        info: this.readDataWarning(error),
        error: this.hideCancel(
          error,
          `Failed to read ${dataType} data from device ${deviceId}. ${this.parsedError(error)}`,
        ),
      }),
      updateFirmware: (error, { deviceId }) => ({
        success: `Firmware on device ${deviceId} updated successfully.`,
        error: error?.includes('reload failed')
          ? this.noSnackBar()
          : `Failed to update firmware. ${this.parsedError(error)}`,
      }),
      downgradeFirmwareDev: (error, { deviceId }) => ({
        success: `Firmware on device ${deviceId} downgraded successfully.`,
        error: `Failed to downgrade firmware. ${this.parsedError(error)}`,
      }),
    },
    connection: {
      connectRfDevice: (error) => ({
        success: undefined,
        error: this.hideCancel(
          error,
          `Failed to connect to device with remote stick. ${this.parsedError(error)}`,
        ),
      }),
      startNativeBleScan: () => ({ success: undefined, error: this.noSnackBar() }),
      reloadDevice: (error, { deviceId }) => ({
        success: `Device ${deviceId} reloaded successfully.`,
        error: `Failed to reload device ${deviceId}. ${this.parsedError(error)}`,
      }),
      writeParameter: (error, { deviceId }) => ({
        success: `Parameter written successfully to device ${deviceId}.`,
        error: `Failed to write parameter to device ${deviceId}. ${this.parsedError(error)}`,
      }),
      resetParameter: (error, { deviceId }) => ({
        success: `Parameter reset successfully on device ${deviceId}.`,
        error: `Failed to reset parameter on device ${deviceId}. ${this.parsedError(error)}`,
      }),
      writeSchedule: (error, { deviceId }, schedule) => ({
        success: `${schedule.scheduleType} schedule written successfully to device ${deviceId}.`,
        error: `Failed to write ${schedule.scheduleType} schedule to device ${deviceId}. ${this.parsedError(error)}`,
      }),
      resetSchedule: (error, { deviceId }, scheduleType) => ({
        success: `${scheduleType} schedule reset successfully on device ${deviceId}.`,
        error: `Failed to reset ${scheduleType} schedule on device ${deviceId}. ${this.parsedError(error)}`,
      }),
      writeVirtualFence: (error, { deviceId }) => ({
        success: `Virtual fence written successfully to device ${deviceId}.`,
        error: `Failed to write virtual fence to device ${deviceId}. ${this.parsedError(error)}`,
      }),
      resetVirtualFence: (error, { deviceId }) => ({
        success: `Virtual fence reset successfully on device ${deviceId}.`,
        error: `Failed to reset virtual fence on device ${deviceId}. ${this.parsedError(error)}`,
      }),
      deleteData: (error, { deviceId }) => ({
        success: `Data deleted successfully from device ${deviceId}.`,
        error: `Failed to delete data from device ${deviceId}. ${this.parsedError(error)}`,
      }),
    },
    app: {
      sync: (error) => ({
        success: `Synchronized successfully with Inventa Web.`,
        error: `Synchronization failed. ${this.parsedError(error)}`,
      }),
      connectToPostgres: (error, { database, host }) => ({
        success: `Connected to postgres database ${database} on ${host} successfully.`,
        error: `Failed to connect to postgres database ${database} on ${host}. ${this.parsedError(error)}`,
      }),
      connectToSqlite: (error) => ({
        success: `Connected to sqlite database successfully.`,
        error: `Failed to connect to sqlite database. ${this.parsedError(error)}`,
      }),
      setMotionBinPath: (error) => ({
        success: `Changed motion files path successfully.`,
        error: this.hideCancel(
          error,
          `Failed to change motion files path. ${this.parsedError(error)}`,
        ),
      }),
      resetMotionBinPath: (error) => ({
        success: `Reset motion files path successfully.`,
        error: this.hideCancel(
          error,
          `Failed to reset motion files path. ${this.parsedError(error)}`,
        ),
      }),
      openPath: (error) => ({
        success: undefined,
        error: error ? `Failed to open path. ${this.parsedError(error)}` : undefined,
      }),
      installDriver: (error, driverRequestType) => ({
        success: undefined, //NOTE driver installation happens in a seperate process. Success cannot be confirmed.
        error: `Failed to install driver for ${driverRequestType}. ${this.parsedError(error)}`,
      }),
      gmak: () => ({ success: undefined, error: this.noSnackBar() }),
      reportIssue: (error) => ({
        success: `Issue reported successfully. Email sent.`,
        error: `Failed to report issue. ${this.parsedError(error)}`,
      }),
    },
    item: {
      getMyRequestedShares: (error) => ({
        success: undefined,
        error: error?.includes('need to be online') ? this.noSnackBar() : error,
      }),
      add: (_error, target) => {
        const success = `Added ${target} successfully.`;
        let error = `Failed to add ${target}. Error: ${_error}`;
        for (const tag of [
          'You may request permission from',
          'is not yet registered by any user',
        ]) {
          if (error.includes(tag)) error = this.noSnackBar();
        }
        return { success, error };
      },
      update: (error, target, { id }) => ({
        success: `Updated ${target} ${id} successfully.`,
        error: `Failed to update ${target} ${id}. ${this.parsedError(error)}`,
      }),
      delete: (error, target, id) => ({
        success: `Deleted ${target} ${id} successfully.`,
        error: `Failed to delete ${target} ${id}. ${this.parsedError(error)}`,
      }),
      activateDevices: (_error, target, ids) => {
        const success =
          ids.length > 1
            ? `Activated airtime for ${target}s ${ids} successfully.`
            : `Activated airtime for ${target} ${ids[0]} successfully.`;
        let error = `Failed to activate airtime. Error: ${_error}`;
        if (error.includes('Monthly service fee: starts today')) error = this.noSnackBar();
        return { success, error };
      },
      deactivateDevices: (_error, target, ids) => {
        const success =
          ids.length > 1
            ? `Deactivated airtime for ${target}s ${ids} successfully.`
            : `Deactivated airtime for ${target} ${ids[0]} successfully.`;
        let error = `Failed to deactivate airtime. Error: ${_error}`;
        if (error.includes('lease confirm')) error = this.noSnackBar();
        return { success, error };
      },
    },
    share: {
      add: (error, target) => ({
        success: `Shared ${target} successfully.`,
        error: `Failed to share ${target}. ${this.parsedError(error)}`,
      }),
      update: (error, target, id) => ({
        success: `Updated ${target} ${id} role successfully.`,
        error: `Failed to update ${target} role. ${this.parsedError(error)}`,
      }),
      delete: (error, target, id) => ({
        success: `Removed access to ${target} ${id} successfully.`,
        error: `Failed to remove access to ${target}. ${this.parsedError(error)}`,
      }),
    },
    deployment: {
      add: (error) => ({
        success: `Added deployment successfully.`,
        error: `Failed to add deployment. ${this.parsedError(error)}`,
      }),
      update: (error) => ({
        success: `Updated deployment successfully.`,
        error: `Failed to update deployment. ${this.parsedError(error)}`,
      }),
      delete: (error) => ({
        success: `Deleted deployment successfully.`,
        error: `Failed to delete deployment. ${this.parsedError(error)}`,
      }),
    },
    event: {
      fileImport: (error, target) => ({
        success: `${target} file imported successfully.`,
        error: `Failed to import ${target} file. ${this.parsedError(error)}`,
      }),
    },
    user: {
      setPreferences: (error) => ({
        success: `Updated preferences successfully.`,
        error: `Failed to update preferences. ${this.parsedError(error)}`,
      }),
    },
    movebank: {
      set: (error) => ({
        success: `Set movebank username successfully.`,
        error: `Failed to set movebank username. ${this.parsedError(error)}`,
      }),
      update: (error) => ({
        success: `Updated movebank username successfully.`,
        error: `Failed to update movebank username. ${this.parsedError(error)}`,
      }),
      delete: (error) => ({
        success: `Removed movebank username successfully.`,
        error: `Failed to remove movebank username. ${this.parsedError(error)}`,
      }),
      addDevices: (error) => ({
        success: `Added movebank forwarding successfully.`,
        error: `Failed to add movebank forwarding. ${this.parsedError(error)}`,
      }),
      removeDevices: (error) => ({
        success: `Removed movebank forwarding successfully.`,
        error: `Failed to remove movebank forwarding. ${this.parsedError(error)}`,
      }),
      syncDevices: (error) => ({
        success: undefined, //NOTE done in movebank restservice
        error: `Failed to synchronize with movebank. ${this.parsedError(error)}`,
      }),
    },
    esri: {
      changeEsriProjectUrl: (error) => ({
        success: `Changed esri forwarding successfully.`,
        error: `Failed to change esri forwarding. ${this.parsedError(error)}`,
      }),
      addEsriProject: (error) => ({
        success: `Added esri forwarding successfully.`,
        error: `Failed to add esri forwarding. ${this.parsedError(error)}`,
      }),
      deleteEsriProject: (error) => ({
        success: 'Removed esri forwarding successfully.',
        error: `Failed to remove esri forwarding. ${this.parsedError(error)}`,
      }),
    },
    esriIdentity: {
      authorize: () => ({ success: undefined, error: this.noSnackBar() }),
      exchange: () => ({ success: undefined, error: this.noSnackBar() }),
      getSession: () => ({ success: undefined, error: this.noSnackBar() }),
      deleteSession: () => ({ success: undefined, error: this.noSnackBar() }),
      getAccessToken: () => ({ success: undefined, error: this.noSnackBar() }),
    },
    notification: {
      addAddress: (error) => ({
        success: `Added address successfully.`,
        error: `Failed to add address. ${this.parsedError(error)}`,
      }),
      addRule: (error) => ({
        success: `Added notification rule successfully.`,
        error: `Failed to add notification rule. ${this.parsedError(error)}`,
      }),
      deleteRule: (error) => ({
        success: `Deleted notification rule successfully.`,
        error: `Failed to delete notification rule. ${this.parsedError(error)}`,
      }),
      deleteRules: (error) => ({
        success: `Deleted notification rules successfully.`,
        error: `Failed to delete notification rules. ${this.parsedError(error)}`,
      }),
    },
    apiKeys: {
      getAllApiKeys: (error) => ({
        success: undefined,
        error: `Failed to load API tokens. ${this.parsedError(error)}`,
      }),
      createApiKey: (error) => ({
        success: `API token created.`,
        error: `Failed to create API token. ${this.parsedError(error)}`,
      }),
      revokeApiKey: (error) => ({
        success: `API token deleted.`,
        error: `Failed to delete API token. ${this.parsedError(error)}`,
      }),
    },
  };

  private static parsedError(error: string) {
    if (!error) return error;
    const regex = new RegExp(`\\b${'Error invoking remote method'}\\b\\s*'[^']*'`, 'g');
    error = error.replace(regex, '').trim();
    if (error.startsWith(':')) error = error.slice(1);
    if (error.includes('rror')) return error;
    return `Error: ${error}`;
  }
}
